import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle, UserCircle2 } from "lucide-react";
import {
  useStaffById,
  useUpdateStaff,
  useSendPasswordReset,
} from "./hooks/useStaff";
import { UpdateUserDto } from "@/types/user";
import { useTeams } from "./hooks/useTeams";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  StaffFormData,
  staffSchema,
  getTeamPlaceholder,
} from "./utils/staffSchema";
import {
  FormField,
  SelectField,
} from "../reservations/guests/components/form/FormFields";
import ImageSelector from "../reservations/guests/components/form/ImageSelector";
import MultiSelect from "@/components/common/MultiSelect";
import LanguageSelect from "@/components/common/LanguageSelect";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { fileService } from "@/services/file-service";
import { useStaffAvailabilityRange } from "@/hooks/useStaffSchedule";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

export const EditStaffPage: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const hotelId = useCurrentHotelId();
  const updateStaff = useUpdateStaff();
  const sendPasswordReset = useSendPasswordReset();
  const { data: teamsData } = useTeams();
  const { data: teams } = teamsData || {};
  const { data: staffData, isLoading } = useStaffById(id!);

  const today = format(new Date(), "yyyy-MM-dd");
  const { data: todayAvailability } = useStaffAvailabilityRange(
    hotelId ?? "",
    today,
    today,
  );

  const isOnSite = useMemo(() => {
    if (!todayAvailability || !staffData) return undefined;
    const staffRelationId = staffData.staff?.id;
    const profileId = staffData.id;
    for (const availability of todayAvailability) {
      if (
        availability.staffId === staffRelationId ||
        availability.profileId === profileId
      ) {
        const day = availability.days[0];
        if (day) return day.isAvailable;
      }
    }
    return undefined;
  }, [todayAvailability, staffData]);

  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      photoId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      position: "staff",
      teams: [] as string[],
      role: "staff",
      preferredLanguage: "en",
    },
  });

  const selectedTeamIds = watch("teams");
  const selectedImage = watch("photoId");

  // Pre-populate form when data is loaded
  useEffect(() => {
    if (staffData) {
      reset({
        photoId: staffData.photoId || "",
        firstName: staffData.firstName || "",
        lastName: staffData.lastName || "",
        email: staffData.email || "",
        phone: staffData.phone || "",
        position: staffData.staff?.position,
        teams: staffData.staff?.userTeams?.map((ut) => ut.teamId) || [],
        role: "staff",
        preferredLanguage: staffData.preferredLanguage || "en",
      });
    }
  }, [staffData, reset]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  const onSubmit = async (data: StaffFormData) => {
    if (!hotelId) return;
    try {
      let photoId: string | undefined = undefined;
      if (selectedPhotoFile) {
        try {
          photoId =
            await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
        } catch (e) {
          console.error("Failed to upload staff photo:", e);
        }
      }

      const userData: UpdateUserDto = {
        photoId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        teams: data.teams,
        position: data.position,
        preferredLanguage: data.preferredLanguage,
      };

      await updateStaff.mutateAsync({ id: id!, data: userData });

      navigate("/staff");
    } catch (err) {
      console.error("Failed to update staff:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  if (!staffData) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div>Staff member not found</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          className="gap-2 pl-0 hover:bg-transparent"
          onClick={() => navigate("/staff")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        <div>
          <h1 className="text-4xl font-semibold text-center mb-8">
            User profile
          </h1>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Avatar Upload */}
            <div className="flex justify-center mb-8">
              <div className="flex flex-col items-center gap-2">
                <ImageSelector
                  defaultIcon={
                    <UserCircle2 className={"size-[72px] stroke-1"} />
                  }
                  overlayContent={
                    <div
                      className={
                        "rounded-full bg-white absolute bottom-0 -translate-x-1/2 left-1/2"
                      }
                    >
                      <PlusCircle className={"size-5"} />
                    </div>
                  }
                  onImageSelect={handleImageUpload}
                  selectedImage={selectedImage || undefined}
                  className="mb-4"
                  imageClassName={"size-[72px] rounded-full"}
                />
                <p className="text-center text-sm text-gray-600">
                  {staffData.firstName} {staffData.lastName}
                </p>
                {isOnSite !== undefined && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "size-2 rounded-full shrink-0",
                        isOnSite ? "bg-green-500" : "bg-zinc-400",
                      )}
                    />
                    <span className="text-xs text-neutral-500">
                      {isOnSite
                        ? t("staff.filters.onSite")
                        : t("staff.filters.notOnSite")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Name and Surname */}
            <div className="grid grid-cols-2 gap-6">
              <FormField
                id="firstName"
                label="NAME"
                required
                register={register}
                error={errors.firstName?.message}
                placeholder="Name"
                className="h-12"
              />
              <FormField
                id="lastName"
                label="SURNAME"
                required
                register={register}
                error={errors.lastName?.message}
                placeholder="Surname"
                className="h-12"
              />
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-2 gap-6">
              <FormField
                id="email"
                label="EMAIL"
                type="email"
                required
                register={register}
                error={errors.email?.message}
                placeholder="@gmail.com"
                className="h-12"
                disabled
              />
              <FormField
                id="phone"
                label="PHONE"
                type="tel"
                required
                register={register}
                error={errors.phone?.message}
                placeholder="000 000 00 00"
                className="h-12"
              />
            </div>

            {/* Position */}
            <FormField
              id="position"
              label="POSITION"
              register={register}
              error={errors.position?.message}
              placeholder="Position"
              className="h-12"
            />

            {/* Role and Team */}
            <div className="grid grid-cols-2 gap-6">
              <SelectField
                id="role"
                label="ROLE"
                register={register}
                options={[{ value: "staff", label: "Staff" }]}
                value="staff"
                className="h-12"
              />

              <LanguageSelect
                label="Language"
                value={watch("preferredLanguage") || "en"}
                onChange={(val) => {
                  if (val) {
                    setValue("preferredLanguage", val);
                  }
                }}
                className="h-12"
                triggerClassName="h-12"
              />
            </div>

            <div className="space-y-2 w-full">
              <label className="text-sm font-medium text-gray-700">TEAMS</label>
              <MultiSelect
                options={
                  teams?.map((team) => ({
                    value: team.id,
                    label: team.name,
                  })) || []
                }
                value={selectedTeamIds || []}
                onValueChange={(value) => setValue("teams", value)}
                placeholder={getTeamPlaceholder(selectedTeamIds)}
                className="w-full h-12"
                containerClassName="!max-w-none"
              />
              {errors.teams?.message && (
                <p className="text-sm text-red-500">{errors.teams.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Send Reset Password Button */}
              <div className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base"
                  onClick={() => sendPasswordReset.mutate(id!)}
                  disabled={sendPasswordReset.isPending}
                >
                  {sendPasswordReset.isPending
                    ? "Sending..."
                    : "Send reset password"}
                </Button>
              </div>

              {/* Save Button */}
              <div className="flex justify-center pt-4">
                <Button
                  type="submit"
                  className="w-full bg-black hover:bg-gray-800 text-white h-12 text-base"
                  disabled={isSubmitting || updateStaff.isPending}
                >
                  {isSubmitting || updateStaff.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
